/**
 * PostGenActions.js
 *
 * Post-generation success screen. Shown after a document is generated.
 * Provides three action paths:
 *  1. Download PDF
 *  2. Email PDF (inline form for recipient)
 *  3. Request eSign (inline form for signer details)
 *
 * Props:
 *  - documentId:    string — generated document ID
 *  - templateName:  string — for display
 *  - pdfFileId:     string — GridFS file ID for download
 *  - onBack:        () => void — return to previous view
 */

import React, { useState } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { toast } from 'react-toastify';

const API_BASE = '/api/external/v1/doqsolv';

const PostGenActions = ({ documentId, templateName, pdfFileId, onBack }) => {
  // Email state
  const [showEmail, setShowEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailing, setEmailing] = useState(false);

  // eSign state
  const [showEsign, setShowEsign] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerMobile, setSignerMobile] = useState('');
  const [esigning, setEsigning] = useState(false);

  // ── Download ──────────────────────────────────────────────────────────

  const handleDownload = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    window.open(`${backendUrl}${API_BASE}/documents/${documentId}/download`, '_blank');
  };

  // ── Email ─────────────────────────────────────────────────────────────

  const handleEmailSend = async () => {
    if (!emailTo.trim()) {
      toast.warning('Please enter a recipient email');
      return;
    }

    setEmailing(true);
    try {
      await apiCall(`${API_BASE}/integrations/email`, {
        method: 'POST',
        data: { document_id: documentId, recipient_email: emailTo.trim() },
      });
      toast.success('Document emailed successfully!');
      setShowEmail(false);
      setEmailTo('');
    } catch (err) {
      toast.error(err.message || 'Failed to send email');
    } finally {
      setEmailing(false);
    }
  };

  // ── eSign ─────────────────────────────────────────────────────────────

  const handleEsignRequest = async () => {
    if (!signerName.trim() || !signerEmail.trim() || !signerMobile.trim()) {
      toast.warning('All signer fields are required');
      return;
    }

    setEsigning(true);
    try {
      await apiCall(`${API_BASE}/integrations/esign`, {
        method: 'POST',
        data: {
          document_id: documentId,
          signer_name: signerName.trim(),
          signer_email: signerEmail.trim(),
          signer_mobile: signerMobile.trim(),
        },
      });
      toast.success('eSign request initiated! Signer will receive an email.');
      setShowEsign(false);
    } catch (err) {
      toast.error(err.message || 'Failed to initiate eSign');
    } finally {
      setEsigning(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="fade-in flex flex-col items-center justify-center h-full min-h-0 py-12">
      {/* Success Icon */}
      <div className="w-20 h-20 mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg">
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-1">Document Generated!</h2>
      <p className="text-sm text-gray-500 mb-8">
        <span className="font-medium text-gray-700">{templateName}</span> is ready
      </p>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl mb-8">
        {/* Download */}
        <button
          onClick={handleDownload}
          className="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 rounded-full bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center transition">
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-800">Download PDF</span>
          <span className="text-xs text-gray-400">Save to your device</span>
        </button>

        {/* Email */}
        <button
          onClick={() => { setShowEmail(!showEmail); setShowEsign(false); }}
          className={`group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border transition-all ${
            showEmail ? 'border-blue-400 shadow-md' : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
          }`}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition ${
            showEmail ? 'bg-blue-100' : 'bg-blue-50 group-hover:bg-blue-100'
          }`}>
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-800">Email PDF</span>
          <span className="text-xs text-gray-400">Send as attachment</span>
        </button>

        {/* eSign */}
        <button
          onClick={() => { setShowEsign(!showEsign); setShowEmail(false); }}
          className={`group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border transition-all ${
            showEsign ? 'border-purple-400 shadow-md' : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
          }`}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition ${
            showEsign ? 'bg-purple-100' : 'bg-purple-50 group-hover:bg-purple-100'
          }`}>
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-800">Request eSign</span>
          <span className="text-xs text-gray-400">Via Aadhaar eSign</span>
        </button>
      </div>

      {/* Email Form (expanded) */}
      {showEmail && (
        <div className="w-full max-w-md bg-white rounded-xl border border-blue-200 shadow-sm p-5 mb-6 fade-in">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Send via Email</h3>
          <div className="flex gap-2">
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            <button
              onClick={handleEmailSend}
              disabled={emailing}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
            >
              {emailing ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* eSign Form (expanded) */}
      {showEsign && (
        <div className="w-full max-w-md bg-white rounded-xl border border-purple-200 shadow-sm p-5 mb-6 fade-in">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">eSign Request</h3>
          <div className="space-y-3">
            <input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Signer Full Name"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
            <input
              type="email"
              value={signerEmail}
              onChange={(e) => setSignerEmail(e.target.value)}
              placeholder="Signer Email"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
            <input
              type="tel"
              value={signerMobile}
              onChange={(e) => setSignerMobile(e.target.value)}
              placeholder="Signer Mobile (10 digits)"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
            <button
              onClick={handleEsignRequest}
              disabled={esigning}
              className="w-full px-4 py-2 text-sm font-medium text-white rounded-lg transition"
              style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}
            >
              {esigning ? 'Initiating…' : 'Request eSign'}
            </button>
          </div>
        </div>
      )}

      {/* Back button */}
      <button
        onClick={onBack}
        className="text-sm text-gray-500 hover:text-gray-700 transition flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Templates
      </button>
    </div>
  );
};

export default PostGenActions;
